/**
 * Generic date/time utilities used by the sessions feature.
 * These are intentionally pure functions with no React or API dependencies.
 */

/**
 * Parses a session date string into a `Date` object.
 *
 * Accepts both ISO 8601 strings (containing `T`) and the space-separated
 * date-part formats returned by the API's various endpoints: `"YYYY-MM-DD
 * HH:MM:SS"` (most endpoints) as well as day-first `"DD-MM-YYYY HH:MM:SS"`
 * (`/sessions/by-day`) — detected by which segment has 4 digits, since
 * assuming ISO order unconditionally silently rolled the day-first date into
 * a nonsense far date (e.g. day="10" read as year 10). Falls back to
 * `new Date()` on any parse error to avoid propagating `Invalid Date`
 * throughout the UI.
 *
 * @param dateStr - Raw date string from the API.
 * @returns A valid `Date`, or `new Date()` if parsing fails.
 */
export const parseSessionDate = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  try {
    if (dateStr.includes('T')) return new Date(dateStr);
    const [datePart, timePart] = dateStr.split(' ');
    const segments = datePart.split('-').map(Number);
    const [year, month, day] = segments[0] > 31 ? segments : [segments[2], segments[1], segments[0]];
    const [hour, minute] = (timePart || '00:00:00').split(':').map(Number);
    const d = new Date(year, month - 1, day, hour, minute);
    return isNaN(d.getTime()) ? new Date() : d;
  } catch {
    return new Date();
  }
};

/**
 * Formats a `Date` as an ISO-8601 date-only string (`"YYYY-MM-DD"`).
 *
 * Uses local wall-clock components (not UTC) so that the resulting string
 * matches the user's local calendar date.
 *
 * @param date - The date to format.
 * @returns A string in `"YYYY-MM-DD"` format.
 */
export const toISODate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Returns the ISO date string for Wednesday of the given Monday-based week.
 * Used to set a sensible default selected day when navigating weeks.
 *
 * @param startDate - The Monday of the week.
 * @returns ISO date string (`"YYYY-MM-DD"`) of Wednesday.
 */
export const getMiddleDay = (startDate: Date): string => {
  const mid = new Date(startDate);
  mid.setDate(startDate.getDate() + 2); // Mon + 2 = Wed
  return toISODate(mid);
};

/**
 * Session statuses that count as an "effective session" — one that actually took place.
 * `active` (scheduled, not yet happened) and `incomplete` (pending payment, session not
 * confirmed) don't count; neither does `cancelled` or `lost`. Only `settled` (paid) and
 * `free` (no-charge session that took place) count.
 */
export const EFFECTIVE_SESSION_STATUSES = ['settled', 'free'] as const;

/**
 * Whether a session status counts as "effective" (actually took place).
 * See `EFFECTIVE_SESSION_STATUSES` for the full criteria.
 */
export const isEffectiveSessionStatus = (status?: string | null): boolean =>
  !!status && (EFFECTIVE_SESSION_STATUSES as readonly string[]).includes(status.toLowerCase());

/**
 * Returns the Monday of the calendar week containing `date`.
 *
 * The sessions tab displays a Mon→Sun week so that the Thu–Sun mobile window
 * always ends with Sunday. Previously this function returned `date - 3` (a
 * rolling centred window) which caused Sunday to appear at the start of the
 * array instead of the end.
 *
 * @param date - The reference date (typically today or the selected day).
 * @returns A new `Date` set to midnight on the Monday of that week.
 */
export const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const daysToMon = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + daysToMon);
  return d;
};
